// components/slides/inkProbe.ts
//
// Deterministic anchor geometry against the page snapshot — the app owns
// correctness, not the model. One job:
//   snapAnchorToInk: FIX a near-miss anchor by moving it to the nearest drawn
//   pixel — so a slightly-off anchor is corrected, not rejected (rejecting
//   drove the agent to drop anchors entirely → no leader line). Only an anchor
//   with no ink anywhere in reach is refused.
//
// Plus the page's ink map, which drives callout LAYOUT (placeNoteInClearSpace).
//
// ⚠️ The two get their pixels from DIFFERENT places, and that is deliberate.
// snapAnchorToInk reads the snapshot into a canvas; the ink map asks the server.
// `/api/assets` 307-redirects an image to a blob SAS URL, so those pixels are
// cross-origin and getImageData throws SecurityError — which means the canvas
// path below silently resolves null on every real deck, and snapAnchorToInk has
// therefore been a no-op in production (it returns the anchor unchanged, so it
// degrades safely rather than breaking placement). The layout map must NOT
// repeat that mistake, so it is computed server-side where the bytes are
// readable. Fixing the canvas path properly means serving snapshots with CORS
// or streaming them instead of redirecting.

import type { SlidePageDoc } from '#types/types';
import { chooseClearFrame, countClearSlots, noteBudget, type Frame } from '#lib/slides/annotationLayout';
import { fetchApi } from '@/kernel/apiFetch';

const PROBE_WIDTH = 512;
const WINDOW_PT = 12;
const INK_LUMINANCE = 235;
const MIN_INK_FRACTION = 0.02;
const SNAP_RADIUS_PT = 48;

interface ProbeData {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

const cache = new Map<string, Promise<ProbeData | null>>();

const loadProbe = (pageId: string, snapshotUrl: string): Promise<ProbeData | null> => {
  const cached = cache.get(pageId);
  if (cached) return cached;
  const p = new Promise<ProbeData | null>((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const w = PROBE_WIDTH;
        const h = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * w));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, w, h);
        resolve({ data: ctx.getImageData(0, 0, w, h).data, width: w, height: h });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = snapshotUrl;
  });
  cache.set(pageId, p);
  return p;
};

const isInkPixel = (probe: ProbeData, x: number, y: number): boolean => {
  if (x < 0 || y < 0 || x >= probe.width || y >= probe.height) return false;
  const i = (y * probe.width + x) * 4;
  const lum = 0.299 * probe.data[i]! + 0.587 * probe.data[i + 1]! + 0.114 * probe.data[i + 2]!;
  return lum < INK_LUMINANCE;
};

const inkFraction = (probe: ProbeData, cx: number, cy: number, r: number): number => {
  let ink = 0;
  let total = 0;
  for (let y = Math.max(0, cy - r); y <= Math.min(probe.height - 1, cy + r); y++) {
    for (let x = Math.max(0, cx - r); x <= Math.min(probe.width - 1, cx + r); x++) {
      if (isInkPixel(probe, x, y)) ink++;
      total++;
    }
  }
  return total > 0 ? ink / total : 0;
};

/** A pixel counts as a real target if it and enough neighbours are ink (rejects
 *  stray antialiased specks). */
const isSolidInk = (probe: ProbeData, x: number, y: number): boolean => {
  if (!isInkPixel(probe, x, y)) return false;
  let n = 0;
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if (isInkPixel(probe, x + dx, y + dy)) n++;
  return n >= 4;
};


interface InkGrid {
  cols: number;
  rows: number;
  /** Summed-area table over cell ink, (cols+1) x (rows+1). */
  integral: Float64Array;
  /** Share of the page that is drawn on, after closing. Drives how many
   *  callouts the page should carry, not just how many fit. */
  occupancy: number;
}

const inkGridCache = new Map<string, Promise<InkGrid | null>>();

/**
 * The page's ink map, from the server.
 *
 * It cannot be measured in the browser: `/api/assets` 307-redirects images to a
 * blob SAS URL, so the pixels are cross-origin and getImageData throws. That is
 * why loadProbe below has been quietly failing on every real deck.
 */
const loadInkGrid = (pageId: string, assetPath: string): Promise<InkGrid | null> => {
  const cached = inkGridCache.get(pageId);
  if (cached) return cached;
  const p = (async (): Promise<InkGrid | null> => {
    try {
      // fetchApi, NOT fetch: a review runs for minutes and will overlap a Clerk
      // token refresh, which answers with the sign-in page wearing an HTTP 200.
      // res.ok is true, res.json() throws, and the page silently loses its ink
      // map — clear-space placement and the callout budget both fall back to
      // bubbles-on-the-drawing. /api/ai/assist and /api/inventory/mutate already
      // carry the retry built for exactly this call (kernel/apiFetch.ts).
      const res = await fetchApi('/api/slides/ink-map', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetPath }),
      });
      if (!res.ok) return null;
      const { cols, rows, grid } = (await res.json()) as { cols: number; rows: number; grid: string };
      const bytes = Uint8Array.from(atob(grid), (c) => c.charCodeAt(0));
      if (!cols || !rows || bytes.length < cols * rows) return null;
      const integral = new Float64Array((cols + 1) * (rows + 1));
      for (let y = 0; y < rows; y++) {
        let run = 0;
        for (let x = 0; x < cols; x++) {
          run += bytes[y * cols + x]! / 255;
          integral[(y + 1) * (cols + 1) + (x + 1)] = integral[y * (cols + 1) + (x + 1)]! + run;
        }
      }
      let occupied = 0;
      for (let i = 0; i < cols * rows; i++) if (bytes[i]! > 0) occupied++;
      return { cols, rows, integral, occupancy: occupied / (cols * rows) };
    } catch {
      return null;
    }
  })();
  // Only a SUCCESS is cached. Caching the null meant one transient 401/500 from
  // /api/slides/ink-map disabled clear-space placement and the callout budget
  // for that page for the whole session, silently falling back to the
  // bubbles-on-the-drawing behaviour this search exists to replace.
  inkGridCache.set(pageId, p);
  void p.then((g) => {
    if (!g) inkGridCache.delete(pageId);
  });
  return p;
};

/** The page's ink oracle for the layout search: share of any rectangle (in page
 *  points) that is drawn content. Null when the page cannot be measured. */
const pageCoverage = async (
  page: SlidePageDoc,
  pageW: number,
  pageH: number
): Promise<((frame: Frame) => number) | null> => {
  if (!page.snapshotUrl) return null;
  const g = await loadInkGrid(page.id, page.snapshotUrl);
  if (!g) return null;
  const stride = g.cols + 1;
  return (frame: Frame): number => {
    const x0 = Math.max(0, Math.min(g.cols, Math.floor((frame.x / pageW) * g.cols)));
    const y0 = Math.max(0, Math.min(g.rows, Math.floor((frame.y / pageH) * g.rows)));
    const x1 = Math.max(x0 + 1, Math.min(g.cols, Math.ceil(((frame.x + frame.w) / pageW) * g.cols)));
    const y1 = Math.max(y0 + 1, Math.min(g.rows, Math.ceil(((frame.y + frame.h) / pageH) * g.rows)));
    const cells = (x1 - x0) * (y1 - y0);
    if (cells <= 0) return 1;
    const s = g.integral;
    const ink =
      s[y1 * stride + x1]! - s[y0 * stride + x1]! - s[y1 * stride + x0]! + s[y0 * stride + x0]!;
    return Math.min(1, Math.max(0, ink / cells));
  };
};

/**
 * Put a callout bubble where it can actually be READ: on blank paper, clear of
 * every other bubble, and as close to its anchor as those allow.
 *
 * The old placement only avoided other bubbles, so on a dense sheet they were
 * pushed around each other and straight onto the drawing they annotate —
 * individually correct, collectively unreadable, covering the very thing under
 * review. A human reviewer parks every bubble in the margin and lets a long
 * leader line do the pointing; scoring slots by ink coverage reproduces that,
 * because margins measure ~0 and win while artwork measures high and loses.
 *
 * Null means no slot is clear enough — see chooseClearFrame.
 */
export async function placeNoteInClearSpace(
  page: SlidePageDoc,
  size: { w: number; h: number },
  anchor: { x: number; y: number },
  blockers: ReadonlyArray<Frame>,
  pageW: number,
  pageH: number
): Promise<{ frame: Frame; coverage: number } | null> {
  const coverage = await pageCoverage(page, pageW, pageH);
  if (!coverage) return null;
  // The measured coverage travels with the frame so the placement can be
  // checked from the outside rather than taken on trust — "the bubble is on
  // blank paper" is the claim this whole search exists to make.
  return chooseClearFrame(size, anchor, blockers, pageW, pageH, coverage);
}

/**
 * How many more callouts this page SHOULD carry: what geometry allows, capped by
 * what a readable sheet can take at this drawing density. Null when the page
 * cannot be measured.
 */
export async function countFreeNoteSlots(
  page: SlidePageDoc,
  size: { w: number; h: number },
  blockers: ReadonlyArray<Frame>,
  pageW: number,
  pageH: number,
  limit = 12
): Promise<number | null> {
  if (!page.snapshotUrl) return null;
  const g = await loadInkGrid(page.id, page.snapshotUrl);
  if (!g) return null;
  const coverage = await pageCoverage(page, pageW, pageH);
  if (!coverage) return null;
  return noteBudget(countClearSlots(size, blockers, pageW, pageH, coverage, limit), g.occupancy);
}

/**
 * Return an anchor that lands on drawn content:
 *  - already on ink → unchanged;
 *  - near ink → SNAPPED to the nearest solid-ink pixel (within SNAP_RADIUS_PT);
 *  - far from any ink → null (the caller asks the agent to re-aim / use a label);
 *  - cannot probe → the input anchor (no-op).
 */
export async function snapAnchorToInk(
  page: SlidePageDoc,
  anchor: { x: number; y: number }
): Promise<{ x: number; y: number } | null> {
  if (!page.snapshotUrl || typeof document === 'undefined') return anchor;
  const probe = await loadProbe(page.id, page.snapshotUrl);
  if (!probe) return anchor;
  const pxPerPt = probe.width / page.width;
  const win = Math.max(2, Math.round(WINDOW_PT * pxPerPt));
  const cx = Math.round(anchor.x * pxPerPt);
  const cy = Math.round(anchor.y * pxPerPt);
  if (inkFraction(probe, cx, cy, win) >= MIN_INK_FRACTION) return anchor;
  // Spiral outward ring by ring for the nearest solid-ink pixel.
  const maxR = Math.round(SNAP_RADIUS_PT * pxPerPt);
  for (let r = 1; r <= maxR; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue; // ring only
        if (isSolidInk(probe, cx + dx, cy + dy)) {
          return { x: (cx + dx) / pxPerPt, y: (cy + dy) / pxPerPt };
        }
      }
    }
  }
  return null;
}
